/**
 * Selection on the reseller /expired delete list (views/pages/Expired.tsx). Not `expired.ts` — that
 * one is the dashboard payout filter, named after a page it no longer serves.
 *
 * The page is server-rendered; each row carries a checkbox (`.expired-select`, value =
 * artwork id) and every cell except the actions cell dispatches `expiredToggle`. The
 * checkbox itself is `pointer-events: none`, so a click anywhere on the row — including on
 * the box — reaches the cell and goes through the one toggle path here. The "Mark N
 * products as removed" button reflects the selection and opens the confirm modal for the
 * selected ids only (`expconfirm:selected:`, read back via `selectedExpiredIds`).
 */
import { registerActions } from "../actionRegistry";
import { modal } from "../ui/modal";
import { getDomElement, queryAll } from "../utils/dom";

const BOX = ".expired-select";

function boxes(): HTMLInputElement[] {
  return [...queryAll<HTMLInputElement>(BOX)];
}

/** Ids currently ticked, ascending. Disabled boxes (already confirmed) never count. */
export function selectedExpiredIds(): number[] {
  return boxes()
    .filter((b) => b.checked && !b.disabled)
    .map((b) => parseInt(b.value, 10))
    .filter((n) => Number.isInteger(n) && n > 0)
    .sort((a, b) => a - b);
}

/** Sync the button label / disabled state and the row highlight with the checkboxes. */
export function refreshExpiredSelection(): void {
  for (const b of boxes()) {
    b.closest("tr")?.classList.toggle("expired-row-selected", b.checked && !b.disabled);
  }
  const n = selectedExpiredIds().length;
  const btn = getDomElement("expired-confirm-selected");
  if (!btn) return;
  const label = getDomElement("expired-confirm-selected-label");
  if (label) label.textContent = `Mark ${String(n)} ${n === 1 ? "product" : "products"} as removed`;
  btn.classList.toggle("expired-confirm-disabled", n === 0);
  btn.setAttribute("aria-disabled", n === 0 ? "true" : "false");
}

/**
 * A click that landed on the checkbox itself has already toggled it natively before it
 * bubbles to the cell's `expiredToggle`. The stylesheet makes that impossible
 * (`pointer-events: none`), but the toggle must be right without it too — the box must not
 * flip back — so the capture-phase listener below remembers such a click and the handler
 * lets the native change stand.
 */
let nativeCheckboxClick = false;
if (typeof document !== "undefined") {
  document.addEventListener(
    "click",
    (e) => {
      nativeCheckboxClick = e.target instanceof HTMLInputElement && e.target.matches(BOX);
    },
    true
  );
}

export function expiredToggle(id: string): void {
  const b = getDomElement<HTMLInputElement>(`expired-select-${id}`);
  if (!b || b.disabled) return;
  if (!nativeCheckboxClick) b.checked = !b.checked;
  nativeCheckboxClick = false;
  refreshExpiredSelection();
}

export function expiredSelectAll(): void {
  for (const b of boxes()) if (!b.disabled) b.checked = true;
  refreshExpiredSelection();
}

export function expiredSelectNone(): void {
  for (const b of boxes()) b.checked = false;
  refreshExpiredSelection();
}

export function expiredConfirmSelected(): void {
  // The disabled look is CSS; this is the guard that matters.
  if (selectedExpiredIds().length === 0) return;
  modal("expconfirm:selected:", "dynamic");
}

/** After a confirmation: those rows are done — mark them, untick them, and lock them out
 *  of any further selection so a second confirm cannot resubmit them. */
export function markExpiredConfirmed(ids: readonly number[]): void {
  for (const id of ids) {
    const target = getDomElement(`confirm-${String(id)}`);
    if (target) target.innerHTML = '<span style="color: #49CF19;">Confirmed</span>';
    const b = getDomElement<HTMLInputElement>(`expired-select-${String(id)}`);
    if (b) {
      b.checked = false;
      b.disabled = true;
    }
  }
  refreshExpiredSelection();
}

registerActions({
  expiredToggle,
  expiredSelectAll,
  expiredSelectNone,
  expiredConfirmSelected,
});
